import { LocalStorageEntities } from '@/constants/LocalStorageEntities';
import type { RouteName } from '@/constants/RouteName';
import getEnv from '../utils/getEnv';
import type { LocationQuery, RouteLocationNormalized, RouteParamsGeneric } from 'vue-router';
import type { Ref, WatchStopHandle } from 'vue';
import { computed, watch } from 'vue';
import { createGlobalState, useLocalStorage } from '@vueuse/core';
import { markRawRecursive } from './queries/markRawRecursive';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { cloneDeep } from 'lodash';
import { UserModule } from '@/store/modules/user';

export type CachedAnalyticsRoute = {
  name: RouteName;
  params?: RouteParamsGeneric;
  query?: LocationQuery;
};

const useCachedAnalyticsRouteGlobalState = createGlobalState(
  (): {
    cachedAnalyticsRoute: Ref<CachedAnalyticsRoute | null>;
  } => {
    const storageKey = computed(() => {
      const user = UserModule.user;

      return user != null
        ? `${LocalStorageEntities.LastUsedAnalyticsRoute}:team-${user.teamId}:user-${user.id}`
        : LocalStorageEntities.LastUsedAnalyticsRoute;
    });

    const cachedAnalyticsRoute = useLocalStorage<CachedAnalyticsRoute | null>(
      storageKey.value,
      null,
      // the default serializer doesn't work for some reason, so we need to use our own
      {
        serializer: {
          read: (value: string): CachedAnalyticsRoute | null => {
            return value ? JSON.parse(value) : null;
          },
          write: (value: CachedAnalyticsRoute | null): string => {
            return JSON.stringify(value);
          },
        },
      },
    );

    return { cachedAnalyticsRoute };
  },
);

export function useLastUsedAnalysis(): {
  cachedAnalyticsRoute: Ref<CachedAnalyticsRoute | null>;
  lastUsedIndexUniqueIdentifier: Ref<string | null>;
  saveLastUsedAnalyticsRoute: (newVal: RouteLocationNormalized | null) => void;
  useUpdateLastUsedAnalyticsRoute: ({
    route,
    itemUnderAnalysis,
  }: {
    route: RouteLocationNormalized;
    itemUnderAnalysis: Ref<IItemUnderAnalysis | null>;
  }) => WatchStopHandle[];
} {
  const { cachedAnalyticsRoute } = useCachedAnalyticsRouteGlobalState();

  const lastUsedIndexUniqueIdentifier = computed((): string | null => {
    return (cachedAnalyticsRoute.value?.params?.indexUniqueIdentifier ?? null) as string | null;
  });

  const { isHSBCEnvironment } = getEnv();

  const saveLastUsedAnalyticsRoute = (newVal: RouteLocationNormalized | null): void => {
    if (isHSBCEnvironment) return;

    if (!newVal) {
      cachedAnalyticsRoute.value = null;
      return;
    }

    cachedAnalyticsRoute.value = {
      name: newVal.name as RouteName,
      params: newVal.params ? markRawRecursive(cloneDeep(newVal.params)) : undefined,
      query: newVal.query ? markRawRecursive(cloneDeep(newVal.query)) : undefined,
    };
  };

  const useUpdateLastUsedAnalyticsRoute = ({
    route,
    itemUnderAnalysis,
  }: {
    route: RouteLocationNormalized;
    itemUnderAnalysis: Ref<IItemUnderAnalysis | null>;
  }): WatchStopHandle[] => {
    const retval: WatchStopHandle[] = [];

    const updateCachedRoute = (
      newRoute: RouteLocationNormalized | null,
      newItemUnderAnalysis: IItemUnderAnalysis | null,
    ): void => {
      if (!newItemUnderAnalysis || !newRoute) return;

      saveLastUsedAnalyticsRoute(newRoute);
    };

    retval.push(
      // eslint-disable-next-line no-restricted-syntax
      watch(
        // only watch the name, params, and query of the route
        // oxlint-disable-next-line typescript/explicit-function-return-type
        [() => route.name, () => route.params, () => route.query],
        () => {
          updateCachedRoute(route, itemUnderAnalysis.value);
        },
        { immediate: true, deep: true },
      ),
    );

    retval.push(
      // eslint-disable-next-line no-restricted-syntax
      watch(
        itemUnderAnalysis,
        (newItemUnderAnalysis): void => {
          updateCachedRoute(route, newItemUnderAnalysis);
        },
        { immediate: true },
      ),
    );

    return retval;
  };

  return {
    cachedAnalyticsRoute,
    lastUsedIndexUniqueIdentifier,
    saveLastUsedAnalyticsRoute,
    useUpdateLastUsedAnalyticsRoute,
  };
}
